// Checkpoint Restore Binder - Shared utility to attach restore/redo UI to ChatArea
// Works with any section using Chat + AIInferenceSystem + ChatArea
// Tools that create checkpoint entries and implement restoreEntry/restoreRedoEntry participate

import { logger } from "../../../core/logger.js";

/**
 * Get the previous user message ID before a response message
 * @param {Chat} chat - The chat instance
 * @param {string} responseMessageId - The response message ID
 * @returns {string|null}
 */
function getPreviousUserMessageId(chat, responseMessageId) {
  if (!chat || !responseMessageId) return null;
  const responseIndex = chat.getMessageIndex?.(responseMessageId);
  if (responseIndex === undefined || responseIndex === -1) return null;
  const messageIds = Array.isArray(chat.messageIDs) ? chat.messageIDs : [];
  for (let i = responseIndex - 1; i >= 0; i -= 1) {
    const messageId = messageIds[i];
    const message = chat.getMessage?.(messageId);
    if (message?.data?.role === "user") return messageId;
  }
  return null;
}

/**
 * Get the root response message ID for a given response (first assistant message after user)
 * @param {Chat} chat - The chat instance
 * @param {string} responseMessageId - The response message ID
 * @returns {string|null}
 */
function getResponseRootMessageId(chat, responseMessageId) {
  if (!chat || !responseMessageId) return null;
  const responseIndex = chat.getMessageIndex?.(responseMessageId);
  if (responseIndex === undefined || responseIndex === -1) return null;
  const messageIds = Array.isArray(chat.messageIDs) ? chat.messageIDs : [];
  let userIndex = -1;
  for (let i = responseIndex - 1; i >= 0; i -= 1) {
    const messageId = messageIds[i];
    const message = chat.getMessage?.(messageId);
    if (message?.data?.role === "user") {
      userIndex = i;
      break;
    }
  }
  if (userIndex === -1) return null;
  for (let i = userIndex + 1; i < messageIds.length; i += 1) {
    const messageId = messageIds[i];
    const message = chat.getMessage?.(messageId);
    if (message?.data?.role === "assistant") return messageId;
    if (message?.data?.role === "user") break;
  }
  return null;
}

/**
 * Get the nearest checkpoint ID for a response (for restore target)
 * @param {Chat} chat - The chat instance
 * @param {string} responseMessageId - The response message ID
 * @param {string|null} responseRootMessageId - Optional root response for multi-turn
 * @returns {string|null}
 */
function getNearestCheckpointIdForResponse(
  chat,
  responseMessageId,
  responseRootMessageId = null
) {
  if (!chat?.checkpointsById || !Array.isArray(chat.checkpointOrder)) return null;
  if (responseRootMessageId) {
    for (const checkpointId of chat.checkpointOrder) {
      const checkpoint = chat.checkpointsById[checkpointId];
      if (checkpoint?.responseMessageId === responseRootMessageId) return checkpointId;
    }
  }
  const responseIndex = chat.getMessageIndex?.(responseMessageId);
  if (responseIndex === undefined || responseIndex === -1) return null;
  for (const checkpointId of chat.checkpointOrder) {
    const checkpoint = chat.checkpointsById[checkpointId];
    if (!checkpoint?.responseMessageId) continue;
    const checkpointIndex = chat.getMessageIndex?.(checkpoint.responseMessageId);
    if (checkpointIndex === undefined || checkpointIndex === -1) continue;
    if (checkpointIndex >= responseIndex) return checkpointId;
  }
  return null;
}

/**
 * Find the last user bubble ID for a user message (from chatArea bubbles)
 * @param {Object} chatArea - ChatArea ref with getBubbles
 * @param {string} messageId - User message ID
 * @param {Map} userMessageLastBubbleIdByMessageId - Optional pre-populated map
 * @returns {string|null}
 */
function findLastUserBubbleId(
  chatArea,
  messageId,
  userMessageLastBubbleIdByMessageId = null
) {
  const map =
    typeof userMessageLastBubbleIdByMessageId === "function"
      ? userMessageLastBubbleIdByMessageId()
      : userMessageLastBubbleIdByMessageId;
  const mapped = map?.get?.(messageId);
  if (mapped) return mapped;
  const bubbles = chatArea?.getBubbles?.() || [];
  const userBubbles = bubbles.filter(
    (b) => b?.messageId === messageId && b?.role === "user"
  );
  if (userBubbles.length === 0) return null;
  return userBubbles[userBubbles.length - 1]?.bubbleId || null;
}

/**
 * Bind checkpoint restore/redo UI to ChatArea
 * @param {Object} params
 * @param {Chat} params.chat - The chat instance (or getter: () => aiInferenceSystem.chat)
 * @param {AIInferenceSystem} params.aiInferenceSystem - For restoreCheckpoint, restoreRedoCheckpoint
 * @param {Object} params.chatArea - ChatArea ref (getBubbles, updateBubble)
 * @param {Object} [params.options]
 * @param {Map|Function} [params.options.userMessageLastBubbleIdByMessageId] - Optional map or getter () => Map
 * @param {Map|Function} [params.options.responseRootMessageIdByResponseMessageId] - Optional map or getter () => Map
 * @param {string} [params.options.logTagPrefix] - Log tag prefix, e.g. "workspace|restore"
 * @param {Function} [params.options.wrapRestoreAction] - (action) => Promise - wrap action (e.g. cancel guard), default: (a) => a()
 * @returns {{ attachRestoreControlsForChat, attachRestoreControlForResponse, hydrateRestoreState, handleRestoreAction, getNearestCheckpointIdForResponse }}
 */
export function bindCheckpointRestoreToChatArea({
  chat,
  aiInferenceSystem,
  chatArea,
  options = {},
}) {
  const {
    userMessageLastBubbleIdByMessageId = null,
    responseRootMessageIdByResponseMessageId = null,
    logTagPrefix = "checkpoint|restore",
    wrapRestoreAction = (action) => action(),
  } = options;

  const restoredUserMessageIds = new Set();
  let activeRestoreState = null;

  function getChat() {
    return typeof chat === "function" ? chat() : chat;
  }

  function setBubblesDisabledFromResponse(responseMessageId, isDisabled) {
    const c = getChat();
    if (!c || !responseMessageId) return;
    const userMessageId = getPreviousUserMessageId(c, responseMessageId);
    if (userMessageId) {
      setBubblesDisabledFromUserMessage(userMessageId, isDisabled);
      return;
    }
    setBubblesDisabledFromResponseMessage(c, responseMessageId, isDisabled);
  }

  function setBubblesDisabledFromUserMessage(userMessageId, isDisabled) {
    if (!chatArea?.getBubbles || !chatArea?.updateBubble) return;
    const bubbles = chatArea.getBubbles();
    const lastUserBubbleId = findLastUserBubbleId(
      chatArea,
      userMessageId,
      userMessageLastBubbleIdByMessageId
    );
    if (!lastUserBubbleId) return;
    const startIndex = bubbles.findIndex((b) => b?.bubbleId === lastUserBubbleId);
    if (startIndex === -1) return;
    bubbles.forEach((bubble, index) => {
      if (bubble?.bubbleId && index > startIndex) {
        chatArea.updateBubble(bubble.bubbleId, { isDisabled });
      }
    });
  }

  function setBubblesDisabledFromResponseMessage(
    c,
    responseMessageId,
    isDisabled
  ) {
    if (!chatArea?.getBubbles || !chatArea?.updateBubble) return;
    const responseIndex = c.getMessageIndex?.(responseMessageId);
    if (responseIndex === undefined || responseIndex === -1) return;
    const bubbles = chatArea.getBubbles();
    let firstResponseBubbleIndex = -1;
    bubbles.forEach((bubble, index) => {
      if (bubble?.messageId === responseMessageId && firstResponseBubbleIndex === -1) {
        firstResponseBubbleIndex = index;
      } else if (bubble?.messageId) {
        const bubbleIndex = c.getMessageIndex?.(bubble.messageId);
        if (bubbleIndex !== undefined && bubbleIndex !== -1 && bubbleIndex >= responseIndex) {
          if (firstResponseBubbleIndex === -1) firstResponseBubbleIndex = index;
        }
      }
    });
    if (firstResponseBubbleIndex !== -1) {
      bubbles.forEach((bubble, index) => {
        if (bubble?.bubbleId && index >= firstResponseBubbleIndex) {
          chatArea.updateBubble(bubble.bubbleId, { isDisabled });
        }
      });
    } else {
      bubbles.forEach((bubble) => {
        if (!bubble?.bubbleId) return;
        const bubbleIndex = bubble.messageId
          ? c.getMessageIndex?.(bubble.messageId)
          : -1;
        if (bubbleIndex !== undefined && bubbleIndex !== -1 && bubbleIndex >= responseIndex) {
          chatArea.updateBubble(bubble.bubbleId, { isDisabled });
        }
      });
    }
  }

  function attachRestoreControlForResponse(
    responseMessageId,
    responseRootMessageId = null
  ) {
    const c = getChat();
    if (!c) return;
    const userMessageId = getPreviousUserMessageId(c, responseMessageId);
    if (!userMessageId) return;
    const rootMap =
      typeof responseRootMessageIdByResponseMessageId === "function"
        ? responseRootMessageIdByResponseMessageId()
        : responseRootMessageIdByResponseMessageId;
    const resolvedRootMessageId =
      responseRootMessageId ||
      rootMap?.get?.(responseMessageId) ||
      getResponseRootMessageId(c, responseMessageId) ||
      responseMessageId;
    const lastBubbleId = findLastUserBubbleId(
      chatArea,
      userMessageId,
      userMessageLastBubbleIdByMessageId
    );
    if (!lastBubbleId || !chatArea?.updateBubble) return;
    const isRestored = restoredUserMessageIds.has(userMessageId);
    const action = () =>
      handleRestoreAction({
        userMessageId,
        responseMessageId,
        responseRootMessageId: resolvedRootMessageId,
      });
    chatArea.updateBubble(lastBubbleId, {
      showRestore: true,
      isRedo: isRestored,
      isRestorePending: false,
      restoreResponseMessageId: responseMessageId,
      restoreRootMessageId: resolvedRootMessageId,
      userMessageId,
      onRestoreClick: () => wrapRestoreAction(action),
    });
  }

  function attachRestoreControlsForChat(chatInstance) {
    const c = chatInstance || getChat();
    if (!c || !Array.isArray(c.messageIDs)) return;
    for (const messageId of c.messageIDs) {
      const message = c.getMessage?.(messageId);
      if (message?.data?.role === "assistant") {
        attachRestoreControlForResponse(messageId);
      }
    }
  }

  function updateRestoreBubbleState(userMessageId, updates = {}) {
    const lastBubbleId = findLastUserBubbleId(
      chatArea,
      userMessageId,
      userMessageLastBubbleIdByMessageId
    );
    if (lastBubbleId && chatArea?.updateBubble) {
      chatArea.updateBubble(lastBubbleId, updates);
    }
  }

  function hydrateRestoreState(chatInstance) {
    restoredUserMessageIds.clear();
    const c = chatInstance || getChat();
    if (!c) return;
    const redoStash = Array.isArray(c.redoStash) ? c.redoStash : [];
    if (redoStash.length === 0) return;
    let latestResponseMessageId = null;
    redoStash.forEach((redoId) => {
      const redoCheckpoint = c.redoById?.[redoId];
      const sourceCheckpointId = redoCheckpoint?.sourceCheckpointId;
      if (!sourceCheckpointId) return;
      const sourceCheckpoint = c.checkpointsById?.[sourceCheckpointId];
      const responseMessageId = sourceCheckpoint?.responseMessageId;
      if (!responseMessageId) return;
      const userMessageId = getPreviousUserMessageId(c, responseMessageId);
      if (userMessageId) {
        restoredUserMessageIds.add(userMessageId);
        latestResponseMessageId = responseMessageId;
      }
    });
    if (latestResponseMessageId) {
      setBubblesDisabledFromResponse(latestResponseMessageId, true);
    }
  }

  async function performRedoForCheckpoint({
    userMessageId,
    responseMessageId,
    checkpointId,
  }) {
    const c = getChat();
    if (!c) return false;
    let redoCheckpointId = null;
    if (checkpointId && c.redoStash) {
      for (let i = c.redoStash.length - 1; i >= 0; i--) {
        const redoId = c.redoStash[i];
        const redoCheckpoint = c.redoById?.[redoId];
        if (redoCheckpoint?.sourceCheckpointId === checkpointId) {
          redoCheckpointId = redoId;
          break;
        }
      }
    }
    if (redoCheckpointId) {
      logger.log(
        { tags: logTagPrefix, color1: "cyan" },
        "Applying redo checkpoint",
        { userMessageId, responseMessageId, checkpointId, redoCheckpointId }
      );
      const redoResult = await aiInferenceSystem.restoreRedoCheckpoint(redoCheckpointId);
      if (redoResult?.success !== true) {
        updateRestoreBubbleState(userMessageId, {
          isRedo: true,
          isRestorePending: false,
        });
        return false;
      }
    } else if (checkpointId) {
      logger.log(
        { tags: logTagPrefix, color1: "yellow" },
        "No matching redo checkpoint found",
        { userMessageId, responseMessageId, checkpointId }
      );
      updateRestoreBubbleState(userMessageId, {
        isRedo: true,
        isRestorePending: false,
      });
      return false;
    } else {
      logger.log(
        { tags: logTagPrefix, color1: "yellow" },
        "No redo checkpoint found",
        { userMessageId, responseMessageId, checkpointId }
      );
      updateRestoreBubbleState(userMessageId, {
        isRedo: false,
        isRestorePending: false,
      });
      return true;
    }
    setBubblesDisabledFromResponse(responseMessageId, false);
    restoredUserMessageIds.delete(userMessageId);
    if (activeRestoreState?.userMessageId === userMessageId) {
      activeRestoreState = null;
    }
    updateRestoreBubbleState(userMessageId, {
      isRedo: false,
      isRestorePending: false,
    });
    return true;
  }

  async function handleRestoreAction({
    userMessageId,
    responseMessageId,
    responseRootMessageId = null,
  }) {
    const c = getChat();
    if (!c) return;
    const isRestored = restoredUserMessageIds.has(userMessageId);

    if (
      !isRestored &&
      activeRestoreState &&
      activeRestoreState.userMessageId !== userMessageId
    ) {
      updateRestoreBubbleState(activeRestoreState.userMessageId, {
        isRedo: false,
        isRestorePending: true,
      });
      updateRestoreBubbleState(userMessageId, {
        isRedo: true,
        isRestorePending: true,
      });
      logger.log(
        { tags: logTagPrefix, color1: "cyan" },
        "Switching restore target: redo previous, then restore new",
        {
          fromUserMessageId: activeRestoreState.userMessageId,
          toUserMessageId: userMessageId,
          fromResponseMessageId: activeRestoreState.responseMessageId,
          toResponseMessageId: responseMessageId,
        }
      );
      const redidPrevious = await performRedoForCheckpoint(activeRestoreState);
      if (!redidPrevious) {
        updateRestoreBubbleState(userMessageId, {
          isRedo: false,
          isRestorePending: false,
        });
        return;
      }
    }

    if (isRestored) {
      updateRestoreBubbleState(userMessageId, {
        isRedo: false,
        isRestorePending: true,
      });
      logger.log(
        { tags: logTagPrefix, color1: "cyan" },
        "Redo requested for active restore",
        { userMessageId, responseMessageId }
      );
      await performRedoForCheckpoint({
        userMessageId,
        responseMessageId,
        checkpointId: getNearestCheckpointIdForResponse(
          c,
          responseMessageId,
          responseRootMessageId
        ),
      });
    } else {
      const checkpointId = getNearestCheckpointIdForResponse(
        c,
        responseMessageId,
        responseRootMessageId
      );
      updateRestoreBubbleState(userMessageId, {
        isRedo: true,
        isRestorePending: true,
      });
      logger.log(
        { tags: logTagPrefix, color1: "cyan" },
        "Restore requested for checkpoint",
        { userMessageId, responseMessageId, responseRootMessageId, checkpointId }
      );
      let restoreResult = { success: true };
      if (checkpointId) {
        restoreResult = await aiInferenceSystem.restoreCheckpoint(checkpointId);
      }
      if (checkpointId && restoreResult?.success !== true) {
        updateRestoreBubbleState(userMessageId, {
          isRedo: false,
          isRestorePending: false,
        });
        return;
      }
      setBubblesDisabledFromResponse(responseMessageId, true);
      restoredUserMessageIds.add(userMessageId);
      activeRestoreState = {
        userMessageId,
        responseMessageId,
        responseRootMessageId,
        checkpointId,
      };
      updateRestoreBubbleState(userMessageId, {
        isRedo: true,
        isRestorePending: false,
      });
    }
  }

  function pruneRestoreStateForRemovedMessages(messageIds = []) {
    const ids = new Set(messageIds);
    if (ids.size === 0) return;
    if (
      activeRestoreState?.responseMessageId &&
      ids.has(activeRestoreState.responseMessageId)
    ) {
      activeRestoreState = null;
    }
    if (
      activeRestoreState?.userMessageId &&
      ids.has(activeRestoreState.userMessageId)
    ) {
      activeRestoreState = null;
    }
    restoredUserMessageIds.forEach((userMessageId) => {
      if (ids.has(userMessageId)) restoredUserMessageIds.delete(userMessageId);
    });
  }

  return {
    attachRestoreControlsForChat,
    attachRestoreControlForResponse,
    hydrateRestoreState,
    handleRestoreAction,
    pruneRestoreStateForRemovedMessages,
    getNearestCheckpointIdForResponse: (responseMessageId, responseRootMessageId) =>
      getNearestCheckpointIdForResponse(
        getChat(),
        responseMessageId,
        responseRootMessageId
      ),
  };
}
